import type { MapboxMapController } from '@xweather/mapsgl'
import { type ReactNode, useCallback } from 'react'
import { create } from 'zustand'
import { combine, persist } from 'zustand/middleware'
import type { DeviceStatus } from './data/devices.ts'
import { IMPACT_CATEGORIES, type ImpactCategory } from './features/impacts/categories.ts'
import type { CustomLayerProps } from './features/layers/customLayers.tsx'
import { type LayerInput, type LayerInputs, LayerInputsSchema } from './features/layers/inputs.tsx'
import type { LayerId, LayerViewId } from './features/layers/layers.tsx'
import type { mapStyles } from './features/map/mapUtils.ts'
import { isValidLastReadBucket } from './features/serviceManagement/serviceMap/lastReadBuckets.ts'
import type { ServiceMapDataFilters, ServiceMapFilterKey } from './features/serviceManagement/serviceMap/ServiceMapFilters.tsx'

export const useMapsgl = create(
	combine({ controller: undefined as MapboxMapController | undefined }, (set) => ({
		setMapController: (controller: MapboxMapController) => set({ controller }),
	})),
)

export const useMap = create(
	combine({ map: undefined as mapboxgl.Map | undefined }, (set) => ({
		setMap: (map: mapboxgl.Map | undefined) => set({ map }),
	})),
)

type Coords = { lat: number; lon: number; z?: number }
export const panels = ['weather', 'alerts', 'charts', 'details'] as const
export const locations = ['all', 'favorites', 'alerting', 'no-alerts-set'] as const
export type LocationFilter = (typeof locations)[number]

export const useLocationFilter = create(
	persist(
		combine({ filter: 'all' as LocationFilter }, (set) => ({
			setFilter: (filter: LocationFilter) => set({ filter }),
		})),
		{ name: 'locationFilter' },
	),
)

export type MapParams = {
	layers?: string[]
	layerGroups?: string[]
	coordinates?: { lat: number; lon: number }
	locationId?: string
	panel?: (typeof panels)[number]
}
export const useMapState = create(
	persist(
		combine(
			{
				mapStyle: 'light' as keyof typeof mapStyles,
				viewState: undefined as Coords | undefined,
				mapParams: null as MapParams | null,
			},
			(set) => ({
				setMapStyle: (mapStyle: keyof typeof mapStyles) => set({ mapStyle }),
				setViewState: (viewState: Coords) => set({ viewState }),
				setMapParams: (mapParams: MapParams) => set({ mapParams }),
			}),
		),
		{
			name: 'mapState',
			partialize: ({ viewState, mapParams }) => ({ viewState, mapParams }),
			merge: (persisted, current) => {
				if (persisted && typeof persisted === 'object') return { ...current, ...persisted }
				const legacy = localStorage.getItem('lastMapState')
				if (legacy) {
					localStorage.removeItem('lastMapState')
					try {
						const { viewState } = JSON.parse(legacy)
						if (viewState) return { ...current, viewState }
					} catch {}
				}
				return current
			},
		},
	),
)

export type ThemeMode = 'light' | 'dark'
export const useThemeMode = create(
	combine({ mode: 'light' as ThemeMode }, (set) => ({
		setMode: (mode: ThemeMode) => set({ mode }),
	})),
)

export const useDrawer = create(
	combine({ selectedDrawer: undefined as 'preferences' | undefined }, (set) => ({
		setSelectedDrawer: (selectedDrawer: 'preferences' | undefined) => set({ selectedDrawer }),
	})),
)

export const useMapSettings = create(
	combine({ open: false }, (set) => ({
		setOpen: (open: boolean) => set({ open }),
	})),
)

export const useLocationList = create(
	combine({ open: false }, (set) => ({
		setOpen: (open: boolean) => set({ open }),
	})),
)

export const useRenderedLocations = create(
	combine({ locationIds: new Set<string>() }, (set) => ({
		add: (locationId: string) =>
			set((state) => (state.locationIds.has(locationId) ? state : { locationIds: new Set(state.locationIds).add(locationId) })),
		remove: (locationId: string) =>
			set((state) => {
				const locationIds = new Set(state.locationIds)
				locationIds.delete(locationId)
				return { locationIds }
			}),
	})),
)

export const useMyLocation = create(
	combine({ coords: undefined as { longitude: number; latitude: number; accuracy: number } | undefined, isFollowing: false }, (set) => ({
		setCoords: (coords: { longitude: number; latitude: number; accuracy: number }) => set({ coords }),
		setIsFollowing: (isFollowing: boolean) => set({ isFollowing }),
	})),
)

export const layerInputsLocalStorageKey = 'layerInputsState'

export const useLayerInputs = create(() => {
	let initialInputs = LayerInputsSchema.decode({})
	try {
		const stored = localStorage.getItem(layerInputsLocalStorageKey)
		if (stored) {
			initialInputs = LayerInputsSchema.parse(JSON.parse(stored))
		}
	} catch (_e) {
		// fallback to default
	}
	return { layerInputs: structuredClone(initialInputs) }
})

const sanitizedLayerInputs = (
	inputs: LayerInputs,
): LayerInputs & Partial<Record<Exclude<LayerViewId, keyof LayerInputs>, Record<string, never>>> => inputs

export const useLayerInput = <TLayer extends LayerViewId>(lvId: TLayer) => {
	const { layerInputs } = useLayerInputs()

	const set = useCallback(
		(update: LayerInput<TLayer> | ((prev: LayerInput<TLayer>) => LayerInput<TLayer>)) =>
			useLayerInputs.setState((prev) => ({
				layerInputs: {
					...prev.layerInputs,
					[lvId]: typeof update === 'function' ? update(sanitizedLayerInputs(prev.layerInputs)[lvId] as LayerInput<TLayer>) : update,
				},
			})),
		[lvId],
	)

	return {
		...sanitizedLayerInputs(layerInputs)[lvId],
		set,
	}
}

export const useServiceMapFilters = create(
	persist(
		combine(
			{
				selectedStatuses: ['ERROR', 'DEGRADED', 'OFFLINE'] satisfies DeviceStatus['status'][] as DeviceStatus['status'][],
				dataFilters: {
					productCodes: [],
					lastReadBuckets: [],
					operators: [],
					customerPartyAccountIds: [],
					subscriptionPartyAccountIds: [],
					countries: [],
				} as ServiceMapDataFilters,
			},
			(set) => ({
				toggleStatus: (status: DeviceStatus['status']) =>
					set((prev) => ({
						selectedStatuses: prev.selectedStatuses.includes(status)
							? prev.selectedStatuses.filter((s) => s !== status)
							: [...prev.selectedStatuses, status],
					})),
				setDataFilter: (key: ServiceMapFilterKey, values: string[]) =>
					set((prev) => ({ dataFilters: { ...prev.dataFilters, [key]: values } })),
				resetDataFilters: () =>
					set({
						dataFilters: {
							productCodes: [],
							lastReadBuckets: [],
							operators: [],
							customerPartyAccountIds: [],
							subscriptionPartyAccountIds: [],
							countries: [],
						},
					}),
			}),
		),
		{
			name: 'serviceMapFilters',
			// lastReadBucket options changed, so need to cleanup old values for now.
			merge: (persisted, current) => {
				const merged = { ...current, ...(persisted as object) }
				return {
					...merged,
					dataFilters: {
						...merged.dataFilters,
						lastReadBuckets: merged.dataFilters.lastReadBuckets.filter(isValidLastReadBucket),
					},
				}
			},
		},
	),
)

export type ImpactsPeriod = 'day' | 'week'
export type ImpactsTab = 'all' | 'favorites'

export const useImpactsDashboard = create(
	persist(
		combine(
			{
				period: 'week' as ImpactsPeriod,
				tab: 'all' as ImpactsTab,
				activeRiskFilters: [...IMPACT_CATEGORIES] as ImpactCategory[],
				hiddenParameters: [] as string[],
			},
			(set) => ({
				setPeriod: (period: ImpactsPeriod) => set({ period }),
				setTab: (tab: ImpactsTab) => set({ tab }),
				setRiskFilters: (activeRiskFilters: ImpactCategory[]) => set({ activeRiskFilters }),
				setHiddenParameters: (hiddenParameters: string[]) => set({ hiddenParameters }),
			}),
		),
		{ name: 'impactsDashboard' },
	),
)

export const useChartParameters = create(
	persist(
		combine({ params: {} as ChartParams }, (set) => ({
			setParameters: (userId: string, deviceId: string, parameters: (string | undefined)[]) =>
				set((state) => ({ params: { ...state.params, [userId]: { ...state.params[userId], [deviceId]: parameters } } })),
			forgetUser: (userId: string) =>
				set((state) => ({ params: Object.fromEntries(Object.entries(state.params).filter(([id]) => id !== userId)) })),
		})),
		{ name: 'chartParameters' },
	),
)
/** userId -> deviceId -> measurementTypeIds */
type ChartParams = Record<string, Record<string, (string | undefined)[]>>

export const useCustomLayers = create(
	combine({ customLayers: {} as CustomLayersState }, (set) => ({
		setCustomLayers: (customLayers: (prev: CustomLayersState) => CustomLayersState) =>
			set((state) => ({ customLayers: customLayers(state.customLayers) })),
	})),
)

export type CustomLayersState = Partial<
	Record<
		LayerId,
		{
			animate: (props: CustomLayerProps) => ReactNode
			render: (props: CustomLayerProps) => ReactNode
			lvId: LayerViewId
			animatable: boolean
		}
	>
>
